import { partnerPushRepo } from "@/data/repos/partnerPushRepo";
import { observability } from "@/lib/observability";

export async function findSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/** The API is told before the browser forgets: an endpoint the browser has
 *  already dropped can no longer be named. A subscription this account never
 *  registered leaves no row to delete, and the endpoint the browser retires here
 *  is pruned by the first push the service refuses for it. */
export async function dropSubscription(subscription: PushSubscription): Promise<void> {
  await partnerPushRepo.unsubscribe(subscription.endpoint);
  await subscription.unsubscribe();
}

const GIVE_UP_AFTER_MS = 3000;

/** Unlike `dropSubscription`, the browser forgets even when the API refuses:
 *  there is no second attempt after sign-out, and the row the API keeps is pruned
 *  by the first push the service refuses for the retired endpoint.
 *  `serviceWorker.ready` is not used: it never settles when the worker failed to
 *  register, and sign-out would wait on it forever. */
async function releaseThisBrowsersSubscription(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const existing = await registration?.pushManager.getSubscription();
    if (!existing) return;
    try {
      await partnerPushRepo.unsubscribe(existing.endpoint);
    } finally {
      await existing.unsubscribe();
    }
  } catch (failure) {
    observability.captureError(failure, { during: "sign-out push release" });
  }
}

/** Runs while the session is still valid, because the API call needs its token.
 *  Never throws and never waits longer than `GIVE_UP_AFTER_MS`: a partner on a
 *  stalled connection must still be able to sign out. */
export async function releaseBrowserPush(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  let giveUpTimer: ReturnType<typeof setTimeout> | undefined;
  const gaveUp = new Promise<void>((resolve) => {
    giveUpTimer = setTimeout(resolve, GIVE_UP_AFTER_MS);
  });
  try {
    await Promise.race([releaseThisBrowsersSubscription(), gaveUp]);
  } finally {
    clearTimeout(giveUpTimer);
  }
}
